import { useTranslation } from "react-i18next";
import { formatPrice, type Currency, type Locale } from "@nh/shared";
import { numberLocaleOf, type ResolvedPrice } from "../shop.catalog";

export interface ProductPriceTagProps {
  price: ResolvedPrice | null;
  /** La moneda que la persona eligió en la barra superior. */
  currency: Currency;
  locale: Locale;
}

/**
 * El precio de la ficha.
 *
 * ── El formato NO se hace aquí ───────────────────────────────────────
 * `formatPrice` vive en `@nh/shared` y es la única que sabe que los
 * precios son centavos enteros. Dividir entre 100 en un componente es
 * como se acaba con dos redondeos distintos en dos pantallas — y con un
 * total que no cuadra con lo que el cliente creyó pagar.
 *
 * ── Sin precio no se pinta un cero ───────────────────────────────────
 * "$0.00" se lee como gratis. Un producto sin precio capturado dice
 * "Precio a consultar", que es lo que de verdad ocurre.
 *
 * ── El tachado solo si es una rebaja ─────────────────────────────────
 * `priceFor` ya descarta un `compareAt` que no sea mayor que el precio:
 * eso no es una rebaja, es un error de captura, y enseñarlo haría dudar
 * del precio de al lado.
 *
 * ── Y cuando la moneda no es la que se pidió ─────────────────────────
 * Un producto puede venderse solo en dólares. La ficha lo DICE en vez de
 * enseñar un símbolo de peso sobre una cifra en dólares: los dos símbolos
 * son "$", así que sin la advertencia el error es invisible.
 */
export function ProductPriceTag({ price, currency, locale }: ProductPriceTagProps) {
  const { t } = useTranslation();
  const numberLocale = numberLocaleOf(locale);

  if (!price) {
    return (
      <p className="text-[18px] font-medium text-ds-ink-3">{t("Precio a consultar")}</p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-3">
        <p className="text-[34px] font-light leading-none tracking-tight text-ds-ink">
          {formatPrice(price.cents, price.currency, numberLocale)}
        </p>

        <span className="text-[12px] font-semibold uppercase tracking-ds-label text-ds-ink-3">
          {price.currency}
        </span>

        {price.compareAtCents !== undefined && (
          <p className="text-[16px] font-light text-ds-ink-3 line-through">
            {formatPrice(price.compareAtCents, price.currency, numberLocale)}
          </p>
        )}
      </div>

      {price.isFallbackCurrency && (
        <p className="mt-2 text-[12px] leading-relaxed text-ds-terracotta">
          {t("Este producto no se vende en {{pedida}}. El precio se muestra en {{usada}}.", {
            pedida: currency,
            usada: price.currency,
          })}
        </p>
      )}
    </div>
  );
}
